import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import DataTable from './DataTable';
import { resolvePlainColumnAliases } from './chartBuilder/LiveDatasetTable';
import { temporalMapsFromColumns } from '../utils/tooltipDetail';
import { formatDateTimeDisplay } from '../utils/dateBucketFormat';

const MAX_CARDS = 200;

/**
 * Columnas que llegan como instante UTC: MIN/MAX sobre fecha en la tabla fuente.
 * Las columnas crudas, los buckets y las salidas de episodios/zonas ya vienen en la
 * hora del dashboard desde el servidor.
 */
export function resolveTablePlusInstantColumns(specColumns) {
  if (!Array.isArray(specColumns) || !specColumns.length) return [];
  const aliases = resolvePlainColumnAliases(specColumns);
  const out = [];
  specColumns.forEach((col, i) => {
    const agg = col?.aggregation;
    if (agg !== 'min' && agg !== 'max') return;
    if (col.dateBucket && col.dateBucket !== 'raw') return;
    if (aliases[i]) out.push(aliases[i]);
  });
  return out;
}

function columnKeys(data, visibleColumns) {
  const fromData = data?.[0] ? Object.keys(data[0]) : [];
  if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) return fromData;
  const allowed = new Set(visibleColumns);
  const picked = fromData.filter((key) => allowed.has(key));
  return picked.length ? picked : fromData;
}

function formatHeaderValue(value, column, maps, timeZone) {
  const temporal = formatDateTimeDisplay(value, {
    dateDisplayFormat: maps.columnDateFormats?.[column],
    dateBucket: maps.columnDateBuckets?.[column],
    timeZone,
  });
  if (temporal != null && temporal !== '') return temporal;
  if (value == null || value === '') return '—';
  return String(value);
}

function TablePlusView({
  data,
  specColumns,
  visibleColumns = null,
  groupColumns = [],
  applyTimezone = true,
  timeZone,
  printCapture = false,
  showTotalColumn = false,
  showTotalRow = false,
  columnDecimals,
  columnDateFormats,
  columnDateBuckets,
  columnColorThresholds,
  columnLabels = {},
  columnDurationFormats,
  columnDurationTooltipFormats,
  columnFilters,
  onColumnFilterChange,
  onClearAllFilters,
  calculatedColumns = [],
}) {
  const zone = applyTimezone && timeZone ? timeZone : undefined;
  const instantColumns = useMemo(() => resolveTablePlusInstantColumns(specColumns), [specColumns]);
  const zoneFor = (column) => (zone && instantColumns.includes(column) ? zone : undefined);
  const maps = useMemo(() => {
    if (columnDateFormats || columnDateBuckets) {
      return {
        columnDateFormats: columnDateFormats || {},
        columnDateBuckets: columnDateBuckets || {},
      };
    }
    if (!specColumns?.length) return { columnDateFormats: {}, columnDateBuckets: {} };
    return temporalMapsFromColumns(specColumns, resolvePlainColumnAliases(specColumns));
  }, [columnDateFormats, columnDateBuckets, specColumns]);

  const keys = useMemo(() => columnKeys(data, visibleColumns), [data, visibleColumns]);
  const groups = useMemo(() => {
    const groupKeys = (groupColumns || []).filter((key) => keys.includes(key));
    return groupKeys;
  }, [groupColumns, keys]);
  const bodyKeys = useMemo(
    () => (groups.length ? keys.filter((key) => !groups.includes(key)) : keys),
    [keys, groups]
  );

  const cards = useMemo(() => {
    if (!groups.length || !data?.length) return [];
    const map = new Map();
    data.forEach((row) => {
      const id = groups.map((key) => String(row?.[key] ?? '')).join('\u0001');
      let card = map.get(id);
      if (!card) {
        card = { id, row, rows: [] };
        map.set(id, card);
      }
      card.rows.push(row);
    });
    return [...map.values()];
  }, [data, groups]);

  if (!data?.length) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sin filas para mostrar
        </Typography>
      </Box>
    );
  }

  const tableProps = {
    printCapture,
    showTotalColumn,
    showTotalRow,
    columnDecimals,
    columnDateFormats: maps.columnDateFormats,
    columnDateBuckets: maps.columnDateBuckets,
    timeZone: zone,
    timeZoneColumns: instantColumns,
    columnColorThresholds,
    columnLabels,
    columnDurationFormats,
    columnDurationTooltipFormats,
    columnFilters,
    onColumnFilterChange,
    onClearAllFilters,
    calculatedColumns,
  };

  if (!groups.length) {
    return <DataTable data={data} visibleColumns={keys} {...tableProps} />;
  }

  const shown = cards.slice(0, MAX_CARDS);
  return (
    <Box sx={{ height: '100%', overflow: printCapture ? 'visible' : 'auto', display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
      {cards.length > shown.length && (
        <Typography variant="caption" color="text.secondary">
          {shown.length} de {cards.length} tarjetas
        </Typography>
      )}
      {shown.map((card) => (
        <Paper key={card.id} variant="outlined" sx={{ p: 1, flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {groups
              .map((key) => {
                const label = columnLabels?.[key] || key;
                return `${label}: ${formatHeaderValue(card.row?.[key], key, maps, zoneFor(key))}`;
              })
              .join(' · ')}
          </Typography>
          <DataTable
            data={card.rows}
            visibleColumns={bodyKeys.length ? bodyKeys : keys}
            fitContent
            {...tableProps}
          />
        </Paper>
      ))}
    </Box>
  );
}

export default TablePlusView;
